import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Archive, Trash2, Cat, User, Calendar, Target, AlertTriangle, Rocket } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionCard } from "@/components/common/SectionCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ChaptersPanel } from "@/components/product/ChaptersPanel";
import { UpdatesHistory } from "@/components/product/UpdatesHistory";
import { moduleFields } from "@/components/product/moduleFields";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { productBadge } from "@/data/productOptions";

const Detail = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5A6B8C]">{label}</p>
    <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#14245F]">{value?.trim() ? value : <span className="text-[#9DAAC6]">Not set</span>}</p>
  </div>
);

export default function ModuleDetail() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [planets, setPlanets] = useState([]);
  const [chapterCount, setChapterCount] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, p, chs] = await Promise.all([api.get("modules", moduleId), api.list("planets"), api.list("chapters")]);
      setModule(m); setPlanets(p);
      setChapterCount(chs.filter((c) => c.module_id === moduleId).length);
    } catch { setModule(null); }
  }, [moduleId]);
  useEffect(() => { load(); }, [load]);

  if (!module) {
    return (
      <div className="py-20 text-center text-[#5A6B8C]" data-testid="module-not-found">
        <p>Loading module…</p>
        <Button variant="outline" className="mt-4 rounded-full border-[#E4E9F3]" onClick={() => navigate("/product")}>Back to Product & Content</Button>
      </div>
    );
  }

  const planet = planets.find((p) => p.id === module.planet_id);
  const accent = planet?.color || "#0EA5A4";
  const planetOptions = planets.map((p) => ({ value: p.id, label: p.name }));
  const completion = Math.max(0, Math.min(100, Number(module.completion) || 0));

  const handleEdit = async (v) => { await api.update("modules", moduleId, v); toast.success("Module updated"); await load(); };
  const handleArchive = async () => { await api.update("modules", moduleId, { status: "Archived" }); toast.success("Module archived"); await load(); };
  const handleDelete = async (reason) => { await api.remove("modules", moduleId, reason); toast.success("Moved to Recycle Bin"); navigate("/product"); };

  return (
    <>
      <button data-testid="back-to-product" onClick={() => navigate("/product")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors hover:text-[#14245F]">
        <ArrowLeft size={16} /> Back to Product & Content
      </button>

      {/* Mission header */}
      <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div className="flex items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ backgroundColor: accent }}>
            <Rocket size={26} strokeWidth={2.2} />
          </span>
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{planet?.name || "Unassigned Planet"}</span>
            <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-[#14245F] md:text-4xl">{module.name}</h1>
            {module.description && <p className="mt-2 max-w-2xl text-sm text-[#5A6B8C]">{module.description}</p>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="edit-module-btn" onClick={() => setEditOpen(true)} className="rounded-full text-white hover:opacity-90" style={{ backgroundColor: accent }}><Pencil size={15} /> Edit</Button>
          {module.status !== "Archived" && <Button variant="outline" data-testid="archive-module-btn" onClick={handleArchive} className="rounded-full border-[#E4E9F3]"><Archive size={15} /> Archive</Button>}
          <Button variant="outline" data-testid="delete-module-btn" onClick={() => setConfirmDelete(true)} className="rounded-full border-[#E4E9F3] text-[#DC2626] hover:bg-[#DC2626]/10"><Trash2 size={15} /> Delete</Button>
        </div>
      </div>

      <SectionCard testid="module-detail-card" accent={accent} className="mb-6">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#5A6B8C]">
          <StatusBadge status={productBadge(module.status)} label={module.status} />
          {module.main_character && <span className="inline-flex items-center gap-1.5"><Cat size={15} strokeWidth={2} /> {module.main_character}</span>}
          <span className="inline-flex items-center gap-1.5"><User size={15} strokeWidth={2} /> {module.owner}</span>
          {module.target_date && <span className="inline-flex items-center gap-1.5"><Calendar size={15} strokeWidth={2} /> {module.target_date}</span>}
          <span className="inline-flex items-center gap-1.5"><Target size={15} strokeWidth={2} /> {module.planned_chapters || 0} planned chapters</span>
        </div>
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs font-medium text-[#5A6B8C]"><span>Completion</span><span className="font-bold" style={{ color: accent }}>{completion}%</span></div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#EEF2FB]">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${completion}%`, backgroundColor: accent }} />
          </div>
        </div>
        {module.current_blocker && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#FB6F5A]/[0.08] px-3 py-2 text-sm font-medium text-[#C2410C]">
            <AlertTriangle size={16} strokeWidth={2} className="mt-0.5 shrink-0" /><span>{module.current_blocker}</span>
          </div>
        )}
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Detail label="Learning objectives" value={module.learning_objectives} />
          <Detail label="Next action" value={module.next_action} />
          <Detail label="Latest update" value={module.latest_update} />
          <Detail label="Notes" value={module.notes} />
        </div>
      </SectionCard>

      <SectionCard testid="module-chapters-card" accent="#F97316" className="mb-6">
        <ChaptersPanel moduleId={moduleId} onChange={load} />
      </SectionCard>

      <SectionCard testid="module-updates-card" accent="#0EA5A4">
        <UpdatesHistory relatedId={moduleId} relatedType="Module" relatedName={module.name} />
      </SectionCard>

      <EntryModal open={editOpen} onOpenChange={setEditOpen} title="Edit Module" submitLabel="Save Changes" fields={moduleFields(planetOptions)} initialValues={module} onSubmit={handleEdit} />
      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title="Delete this module?" onConfirm={handleDelete} softDelete linkedCount={chapterCount} />
    </>
  );
}
